import { motion } from "framer-motion";
import { Download, Eye, Target, BadgeCheck, Flame, Package, Ship } from "lucide-react";
import { COMPANY } from "@/constants/company";

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: EASE },
});

const ADVANTAGES = [
  "Comprehensive expertise in international petroleum & commodities trading and shipping / agency services",
  "Strong market presence and connections across Asia-Pacific — especially the Pacific Islands, China and Indonesia",
  "Streamlined supply and demand, arranged directly with suppliers and end-users through efficient logistics",
  "Dedicated operational support with prompt response",
  "Dedicated service delivered with professionalism and passion",
  "Strong financial support from leading international and private banks and international traders",
];

const LINES = [
  {
    icon: Flame,
    name: "Petroleum",
    items: ["Crude", "Fuel Oil", "Gasoil", "Gasoline", "Bitumen", "Petrochemical"],
    note: "With strong support from our supply partners, we blend products to the special needs and specifications of our receivers and end users.",
  },
  {
    icon: Package,
    name: "Commodities",
    items: ["Coal", "Petroleum Coke / Green Coke", "Rubber", "Metals (incl. finished & customised)", "Sugar", "Rice & Grains", "Fats & Edible Oils"],
    note: null,
  },
  {
    icon: Ship,
    name: "Services",
    items: ["Cargo sale / purchase brokerage with 3rd-party finance service", "Outsourced inspection services", "Shipping agency"],
    note: "Full-spectrum port agency for vessels calling into ports in South-East Asia and China, with bunker, spares and water supply.",
  },
];

export default function CompanyProfile() {
  return (
    <section className="border-b border-navy-600/60 bg-navy-800/40" data-testid="company-profile-section">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-12">
        <motion.div {...reveal()} className="flex flex-wrap items-end justify-between gap-6 border-b border-navy-600/60 pb-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand">Company Profile</p>
            <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">About Excellent Traders</h2>
            <p className="mt-2 font-mono text-xs text-slate-500">
              Incorporated 07 Aug 2022 · UEN 202227580C · Singapore
            </p>
          </div>
          <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="inline-block">
            <a
              href="/docs/Excellent-Traders-Company-Profile.docx"
              download
              data-testid="company-profile-download"
              className="inline-flex items-center gap-2 rounded-md border border-navy-600 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors duration-200 hover:border-brand/60 hover:text-brand"
            >
              <Download className="h-4 w-4" /> Download company profile
            </a>
          </motion.span>
        </motion.div>

        <div className="mt-14 grid gap-12 lg:grid-cols-5">
          <motion.div {...reveal(0.05)} className="space-y-5 text-base leading-relaxed text-slate-300 lg:col-span-3">
            <p data-testid="profile-about-p1">
              EXCELLENT TRADERS PTE. LTD. (ETPL) is incorporated in 07 AUG 2022 with UEN 202227580C, with our
              base in the exciting and effective financial and commodities hub of Singapore. Despite being a newly
              formed company, our founding members bring with them close to a combined 6 decades of management,
              trading, and financial expertise and industry experiences and network.
            </p>
            <p data-testid="profile-about-p2">
              The objective of ETPL is connecting global supply / demand and services for the petroleum,
              commodities and shipping industry, specialising in Asia. Our core focus is sourcing, supplying and
              trading / brokerage of petroleum products, commodities and related shipping agency services. With a
              strong understanding of the diversity of the international market, we execute our business with
              valuable insights and strategic planning under our strict in-house compliance and QHSSE policy.
              With extensive industry experience, ETPL is proficient in handling petroleum, commodities and
              shipping / agency services in accordance with international standards — and onwards, we will
              continue to maximise opportunities for growth and distribution of our products and services,
              regionally and globally.
            </p>
          </motion.div>

          <div className="space-y-5 lg:col-span-2">
            <motion.div {...reveal(0.1)} className="spotlight-card rounded-lg border border-navy-600 bg-navy-800 p-7" data-testid="profile-vision-card">
              <Eye className="relative z-10 h-7 w-7 text-brand" />
              <h3 className="relative z-10 mt-4 font-mono text-xs uppercase tracking-[0.3em] text-brand">Vision</h3>
              <p className="relative z-10 mt-3 text-sm leading-relaxed text-slate-300">
                ETPL aspires to be a premier contributor in the industry of petroleum, commodities and shipping
                through our expertise and professionalism.
              </p>
            </motion.div>
            <motion.div {...reveal(0.15)} className="spotlight-card rounded-lg border border-navy-600 bg-navy-800 p-7" data-testid="profile-mission-card">
              <Target className="relative z-10 h-7 w-7 text-brand" />
              <h3 className="relative z-10 mt-4 font-mono text-xs uppercase tracking-[0.3em] text-brand">Mission</h3>
              <p className="relative z-10 mt-3 text-sm leading-relaxed text-slate-300">
                To be a trustworthy and reliable long-term trading partner with valued counterparties by providing
                premium service and quality products — transacting directly with the world's largest multinational
                oil majors, global trading houses and national companies, from producers and refiners to receivers.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="mt-20">
          <motion.h3 {...reveal()} className="text-2xl font-bold text-white sm:text-3xl">Competitive advantages</motion.h3>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ADVANTAGES.map((a, i) => (
              <motion.div
                key={a}
                {...reveal(i * 0.05)}
                className="flex items-start gap-3 rounded-lg border border-navy-600 bg-navy-800 p-5 transition-colors duration-300 hover:border-brand/50"
                data-testid={`profile-advantage-${i}`}
              >
                <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                <span className="text-sm leading-relaxed text-slate-300">{a}</span>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-20">
          <motion.h3 {...reveal()} className="text-2xl font-bold text-white sm:text-3xl">Products &amp; services</motion.h3>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {LINES.map((l, i) => (
              <motion.div
                key={l.name}
                {...reveal(i * 0.08)}
                className="spotlight-card rounded-lg border border-navy-600 bg-navy-800 p-7 transition-colors duration-300 hover:border-brand/50"
                data-testid={`profile-line-${i}`}
              >
                <l.icon className="relative z-10 h-8 w-8 text-brand" />
                <h4 className="relative z-10 mt-5 text-lg font-bold text-white">{l.name}</h4>
                <ul className="relative z-10 mt-4 space-y-2 text-sm text-slate-400">
                  {l.items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                      {it}
                    </li>
                  ))}
                </ul>
                {l.note && <p className="relative z-10 mt-4 text-xs leading-relaxed text-slate-500">{l.note}</p>}
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div {...reveal()} className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-lg border border-navy-600 bg-navy-800 p-6 text-sm text-slate-300" data-testid="profile-contact-strip">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand">Contact</span>
          <span>{COMPANY.address}</span>
          <a href={`mailto:${COMPANY.email}`} className="text-brand hover:underline">{COMPANY.email}</a>
          <span className="font-mono text-xs text-slate-500">Victor Chan · +65 9450 2088</span>
        </motion.div>
      </div>
    </section>
  );
}
